import { computed, effect, Injectable, signal } from '@angular/core';
import { STORAGE_KEYS } from '../utils/consts';

export type ThemeMode = 'light' | 'dark' | 'system';
export type ThemePalette = 'violet' | 'azure' | 'rose' | 'green';

export interface ThemeModeOption {
  value: ThemeMode;
  labelKey: string;
  icon: string;
}

export interface ThemePaletteOption {
  value: ThemePalette;
  labelKey: string;
  /** Representative swatch color (for preview in the UI). */
  swatch: string;
}

const DEFAULT_MODE: ThemeMode = 'system';
const DEFAULT_PALETTE: ThemePalette = 'violet';

const ALL_MODE_CLASSES: readonly string[] = ['mode-light', 'mode-dark'];
const ALL_PALETTE_CLASSES: readonly string[] = [
  'palette-azure',
  'palette-rose',
  'palette-green',
];

@Injectable({ providedIn: 'root' })
export class ThemeService {
  private readonly _mode = signal<ThemeMode>(DEFAULT_MODE);
  private readonly _palette = signal<ThemePalette>(DEFAULT_PALETTE);

  readonly mode = computed(() => this._mode());
  readonly palette = computed(() => this._palette());

  readonly availableModes: readonly ThemeModeOption[] = [
    { value: 'system', labelKey: 'settings.theme.mode.system', icon: 'brightness_auto' },
    { value: 'light',  labelKey: 'settings.theme.mode.light',  icon: 'light_mode' },
    { value: 'dark',   labelKey: 'settings.theme.mode.dark',   icon: 'dark_mode' },
  ];

  readonly availablePalettes: readonly ThemePaletteOption[] = [
    { value: 'violet', labelKey: 'settings.theme.palette.violet', swatch: '#7c3aed' },
    { value: 'azure',  labelKey: 'settings.theme.palette.azure',  swatch: '#0284c7' },
    { value: 'rose',   labelKey: 'settings.theme.palette.rose',   swatch: '#e11d48' },
    { value: 'green',  labelKey: 'settings.theme.palette.green',  swatch: '#16a34a' },
  ];

  constructor() {
    effect(() => {
      const mode    = this._mode();
      const palette = this._palette();
      const html    = document.documentElement;

      // Remove all mode classes then apply the chosen one (if not system)
      ALL_MODE_CLASSES.forEach((c) => html.classList.remove(c));
      if (mode !== 'system') {
        html.classList.add(`mode-${mode}`);
      }

      // Remove all palette classes then apply the chosen one (if not default)
      ALL_PALETTE_CLASSES.forEach((c) => html.classList.remove(c));
      if (palette !== 'violet') {
        html.classList.add(`palette-${palette}`);
      }
    });
  }

  initializeTheme(): void {
    const storedMode    = this.getPersistedMode();
    const storedPalette = this.getPersistedPalette();
    this._mode.set(storedMode ?? DEFAULT_MODE);
    this._palette.set(storedPalette ?? DEFAULT_PALETTE);
  }

  setMode(mode: ThemeMode): void {
    this._mode.set(mode);
    this.persist(STORAGE_KEYS.THEME_MODE, mode);
  }

  setPalette(palette: ThemePalette): void {
    this._palette.set(palette);
    this.persist(STORAGE_KEYS.THEME_PALETTE, palette);
  }

  isSupportedMode(value: unknown): value is ThemeMode {
    return (
      typeof value === 'string' &&
      this.availableModes.some((m) => m.value === value)
    );
  }

  isSupportedPalette(value: unknown): value is ThemePalette {
    return (
      typeof value === 'string' &&
      this.availablePalettes.some((p) => p.value === value)
    );
  }

  private getPersistedMode(): ThemeMode | null {
    try {
      const raw = localStorage.getItem(STORAGE_KEYS.THEME_MODE);
      return this.isSupportedMode(raw) ? raw : null;
    } catch (error) {
      console.error('Failed to load persisted theme mode:', error);
      return null;
    }
  }

  private getPersistedPalette(): ThemePalette | null {
    try {
      const raw = localStorage.getItem(STORAGE_KEYS.THEME_PALETTE);
      return this.isSupportedPalette(raw) ? raw : null;
    } catch (error) {
      console.error('Failed to load persisted theme palette:', error);
      return null;
    }
  }

  private persist(key: string, value: string): void {
    try {
      localStorage.setItem(key, value);
    } catch (error) {
      console.error(`Failed to persist theme key "${key}":`, error);
    }
  }
}
